/**
 * Build-time configuration. Vite exposes env vars with the `CC_` prefix (vite.config.ts
 * `envPrefix`); every one has a default so a bare `vite build` works. The README lists them.
 */
export type LogLevelName = 'debug' | 'info' | 'warn' | 'error';

export interface PhoneEnv {
  /** base URL of the api service (`https://api.<domain>`); default derived from the phone host */
  apiBase: string;
  logLevel: LogLevelName;
  /** lab only: send the INVITE from the browser (sip.js Inviter) instead of relay `dial` */
  directDial: boolean;
  /** browser policy name shown when pop-ups are blocked */
  popupPolicyName: string;
}

const LEVELS: ReadonlySet<string> = new Set(['debug', 'info', 'warn', 'error']);

/** `phone.example.com` -> `https://api.example.com`; anything else -> same origin */
export function deriveApiBase(hostname: string, protocol = 'https:'): string {
  const m = /^phone\.(.+)$/.exec(hostname);
  if (m) return `${protocol}//api.${m[1]}`;
  return `${protocol}//${hostname}`;
}

export function readEnv(
  raw: Record<string, string | boolean | undefined> = import.meta.env as Record<string, string | boolean | undefined>,
  loc: { hostname: string; protocol: string } = globalThis.location,
): PhoneEnv {
  const str = (k: string): string | undefined => {
    const v = raw[k];
    return typeof v === 'string' && v.length > 0 ? v : undefined;
  };
  const level = str('CC_LOG_LEVEL');
  const protocol = loc.protocol === 'http:' ? 'http:' : 'https:';
  return {
    apiBase: (str('CC_API_BASE') ?? deriveApiBase(loc.hostname, protocol)).replace(/\/+$/, ''),
    logLevel: level && LEVELS.has(level) ? (level as LogLevelName) : 'info',
    directDial: str('CC_DIRECT_DIAL') === 'true',
    popupPolicyName: str('CC_POPUP_POLICY_NAME') ?? 'PopupsAllowedForUrls',
  };
}

export const APP_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0';
